import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/dash/AppShell";
import { PublicLinkCard } from "@/components/settings/PublicLinkCard";
import { LogoUploader } from "@/components/settings/LogoUploader";
import { BrandColorPicker } from "@/components/settings/BrandColorPicker";
import { ServicePicker } from "@/components/services/ServicePicker";
import { BasePrices } from "@/components/settings/BasePrices";
import { ChangePassword } from "@/components/settings/ChangePassword";
import { useBilling } from "@/hooks/useBilling";
import { StateSelect } from "@/components/StateSelect";
import { PreviewSettings } from "@/components/settings/PreviewSettings";
import { InvoiceList } from "@/components/settings/InvoiceList";

const TABS = [["business", "Business"], ["services", "Services & Pricing"], ["link", "Quote Link"], ["account", "Account"], ["billing", "Billing"]];

const Section = ({ title, sub, children }) => (
  <section className="rounded-2xl border bg-card p-5 shadow-soft sm:p-6">
    <h2 className="text-base font-semibold text-foreground">{title}</h2>
    {sub && <p className="mt-0.5 text-sm text-muted-foreground">{sub}</p>}
    <div className="mt-5">{children}</div>
  </section>
);

const Field = ({ label, hint, ...props }) => (
  <label className="block">
    <span className="text-sm font-medium text-foreground">{label}</span>
    <Input {...props} className="mt-1.5 h-12 rounded-xl bg-card text-base focus-visible:ring-0 focus-visible:border-[color:var(--brand)]" />
    {hint && <span className="mt-1 block text-xs text-muted-foreground">{hint}</span>}
  </label>
);

const planText = (sub) => {
  if (sub.status === "unverified") return "Confirm your email to start your 14-day free trial";
  if (sub.status === "comped") return `Complimentary access${sub.comped_until ? ` · until ${new Date(sub.comped_until).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : ""}`;
  if (sub.status === "trialing") return `Free trial · ${sub.trial_days_left} days left`;
  if (sub.status === "active") return `Standard plan · $99/month${sub.current_period_end ? ` · renews ${new Date(sub.current_period_end).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}`;
  return sub.status === "past_due" ? "Payment failed · please update your card" : "Subscription inactive";
};

const Billing = ({ sub }) => {
  const { busy, startCheckout, openPortal } = useBilling();
  const subscribed = sub.has_billing && ["active", "past_due"].includes(sub.status === "comped" ? sub.stripe_status : sub.status);
  const permanentComp = sub.status === "comped" && !sub.comped_until;
  return (
    <Section title="Billing" sub="One plan with everything included. Cancel anytime.">
      <p className="text-3xl font-bold tracking-tight text-foreground">$99<span className="text-base font-medium text-muted-foreground">/month</span></p>
      <p className="mt-1 text-sm text-muted-foreground" data-testid="settings-plan-status">{planText(sub)}</p>
      {(subscribed || !permanentComp) && <button type="button" onClick={subscribed ? openPortal : startCheckout} disabled={busy} data-testid={subscribed ? "manage-billing-button" : "settings-subscribe-button"}
        className="mt-5 flex h-12 items-center gap-2 rounded-xl bg-foreground px-5 text-sm font-semibold text-background disabled:opacity-50">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} {subscribed ? "Manage billing" : "Subscribe · $99/month"}
      </button>}
    </Section>
  );
};

export default function Settings() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("business");
  const [form, setForm] = useState({
    company_name: user.company_name, phone: user.phone || "", trust_line: user.trust_line || "", brand_color: user.brand_color,
    services: user.services, base_prices: user.base_prices || {},
    city: user.city || "", state: user.state || "", service_area: user.service_area || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (form.services.length === 0) { toast.error("Select at least one service"); return; }
    if (!form.city.trim() || !form.state) { setTab("business"); toast.error("Add your city and state"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/settings", { ...form, phone: form.phone || null, trust_line: form.trust_line || null, service_area: form.service_area.trim() || null });
      setUser(data);
      toast.success("Settings saved");
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSaving(false); }
  };

  return (
    <AppShell>
      <div style={{ "--brand": form.brand_color }} data-testid="settings-page">
        <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">Settings</h1>
        <Tabs value={tab} onValueChange={setTab} className="mt-5">
          <div className="-mx-4 overflow-x-auto px-4 [scrollbar-width:none] sm:mx-0 sm:px-0">
            <TabsList className="h-11 w-max gap-1 rounded-xl bg-muted p-1">
              {TABS.map(([key, label]) => (
                <TabsTrigger key={key} value={key} data-testid={`settings-tab-${key}`}
                  className="h-9 rounded-lg px-3.5 text-sm data-[state=active]:bg-card data-[state=active]:shadow-soft">{label}</TabsTrigger>
              ))}
            </TabsList>
          </div>
          <TabsContent value="business" className="mt-5 space-y-4">
            <Section title="Business profile" sub="Shown to homeowners at the top of your quote form.">
              <div className="space-y-5">
                <LogoUploader logoUrl={user.logo_url} companyName={form.company_name || user.company_name} onUploaded={(logo_url) => setUser({ ...user, logo_url })} />
                <Field label="Company name" value={form.company_name} onChange={(e) => set("company_name")(e.target.value)} data-testid="settings-company-name-input" />
                <Field label="Trust line" maxLength={80} placeholder="Licensed & Insured · Serving South Jersey" hint="Short line shown under your name on the quote form."
                  value={form.trust_line} onChange={(e) => set("trust_line")(e.target.value)} data-testid="settings-trust-line-input" />
                <Field label="Business phone" type="tel" value={form.phone} onChange={(e) => set("phone")(e.target.value)} data-testid="settings-phone-input" />
                <div>
                  <span className="text-sm font-medium text-foreground">Brand color</span>
                  <div className="mt-2"><BrandColorPicker value={form.brand_color} onChange={set("brand_color")} /></div>
                </div>
              </div>
            </Section>
            <Section title="Location" sub="The AI uses your city and state as the local price reference.">
              <div className="space-y-5">
                <div className="grid grid-cols-[1fr_140px] gap-3">
                  <Field label="City" value={form.city} maxLength={80} onChange={(e) => set("city")(e.target.value)} data-testid="settings-city-input" />
                  <label className="block">
                    <span className="text-sm font-medium text-foreground">State</span>
                    <StateSelect value={form.state} onChange={set("state")} data-testid="settings-state-select"
                      className="mt-1.5 h-12 rounded-xl border bg-card px-3 text-base text-foreground focus:border-[color:var(--brand)] focus:outline-none" />
                  </label>
                </div>
                <Field label="Service area" maxLength={120} placeholder="e.g. Camden & Burlington counties" hint="Optional. Towns or counties you cover."
                  value={form.service_area} onChange={(e) => set("service_area")(e.target.value)} data-testid="settings-service-area-input" />
              </div>
            </Section>
          </TabsContent>
          <TabsContent value="services" className="mt-5 space-y-4">
            <Section title="Services you offer" sub="Tap to select. Homeowners only see these on your quote form.">
              <ServicePicker services={form.services} onChange={set("services")} />
            </Section>
            <Section title="Base prices" sub="Optional. Leave blank to use typical local rates.">
              <BasePrices services={form.services} prices={form.base_prices} onPrices={set("base_prices")} />
            </Section>
          </TabsContent>
          <TabsContent value="link" className="mt-5 space-y-4">
            <PublicLinkCard slug={user.slug} onSlugSaved={(u) => setUser({ ...user, slug: u.slug })} />
            <PreviewSettings />
          </TabsContent>
          <TabsContent value="account" className="mt-5">
            <Section title="Account">
              <p className="text-sm text-muted-foreground">Signed in as</p>
              <p className="text-sm font-medium text-foreground" data-testid="settings-account-email">{user.email}</p>
              <div className="mt-5 flex flex-wrap items-start gap-2">
                <ChangePassword />
                <button type="button" onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-button"
                  className="flex h-12 items-center gap-2 rounded-xl border px-4 text-sm font-semibold text-foreground hover:bg-muted">
                  <LogOut className="h-4 w-4" /> Log out
                </button>
              </div>
            </Section>
          </TabsContent>
          <TabsContent value="billing" className="mt-5 space-y-4"><Billing sub={user.subscription} />{user.subscription.has_billing && <InvoiceList />}</TabsContent>
        </Tabs>
        {(tab === "business" || tab === "services") && (
          <button type="button" onClick={save} disabled={saving} data-testid="settings-save-button"
            className="sticky bottom-20 z-20 mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[color:var(--brand)] text-base font-semibold text-white shadow-lift transition-[opacity,transform] active:scale-[0.98] disabled:opacity-50 lg:bottom-6 lg:w-auto lg:px-8">
            {saving && <Loader2 className="h-5 w-5 animate-spin" />} Save changes
          </button>
        )}
      </div>
    </AppShell>
  );
}
